<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import axios from "axios";
import Swal from "sweetalert2";
import Dropdown from 'primevue/dropdown';
import { useGradeStore } from '@/stores/useGradeStore.js';
import MultiSelect from 'primevue/multiselect';
import Calendar from 'primevue/calendar';
import InputText from 'primevue/inputtext';
import InputSwitch from 'primevue/inputswitch';
import DatePicker from 'primevue/datepicker';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

export default {
  name: "edit-exam",
  inject: {
    courseCtx: { default: null },
  },
  data() {
    return {
      v$: useVuelidate(),
      searchQuery: '',
      examId: this.$route.params.id,
      gradeSlug: this.$route.params.gradeSlug,
      courseId: this.$route.params.courseId ?? null,
      title: "",
      date: null,
      selectedQuestions: [],
      questions: [],
      from_time: null,
      to_time: null,
      lesson: null,
      lessons: [],
      unit: null,
      units: [],
      branch: null,
      branches: [],
      duration_required: false,
      formSending: false,
      loading: true,
    };
  },
  validations() {
    return {
      title: { required },
      date: { required },
      question_ids: { required },
    };
  },
  components: {
    Breadcrumb,
    Dropdown,
    MultiSelect,
    Calendar,
    InputText,
    InputSwitch,
    DatePicker,
    DataTable,
    Column
  },
  computed: {
    isCourseScope() { return !!this.courseId; },
    basePath() {
      return this.isCourseScope ? `/courses/${this.courseId}/exams` : `/${this.gradeSlug}/exams`;
    },
    breadcrumbs() {
      if (this.isCourseScope) {
        const base = this.courseCtx?.breadcrumbBase ?? [];
        return [...base, { name: "الامتحانات", path: this.basePath }, { name: "تعديل امتحان", path: `${this.basePath}/edit/${this.examId}` }];
      }
      return [
        { name: 'الرئيسية', path: '/' },
        { name: "الامتحانات", path: this.basePath },
        { name: "تعديل امتحان", path: `/${this.gradeSlug}/exam/edit/${this.examId}` },
      ];
    },
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          || this.gradeStore.allGrades.find(g => g.slug === slug)
          || null;
    },
    gradeId() { return this.resolvedGrade?.id ?? null; },
    customLabelBranch() {
      return option => option ? `${option.title} (${option.description})` : '';
    },
    customLabelUnit() {
      return option => option ? `${option.title} (${option.description})` : '';
    },
    customLabelLesson() {
      return option => option ? `${option.title} (${option.description})` : '';
    },
    duration() {
      if (!this.from_time || !this.to_time) return null;

      const getDateObject = (value) => (value instanceof Date ? value : new Date(value));

      let start = getDateObject(this.from_time);
      let end = getDateObject(this.to_time);

      const today = new Date();
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), start.getHours(), start.getMinutes());
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), end.getHours(), end.getMinutes());

      let diffMs = end - start;
      if (diffMs < 0) {
        end.setDate(end.getDate() + 1);
        diffMs = end - start;
      }

      const diffMinutes = Math.floor(diffMs / 60000);
      if (diffMinutes <= 0 || diffMinutes > 1440) return null;

      return diffMinutes;
    },
    question_ids: {
      get() {
        return this.selectedQuestions.map(q => q.id);
      },
      set(ids) {
        this.selectedQuestions = this.questions.filter(q => ids.includes(q.id));
      }
    },
    totalDegree() {
      return this.selectedQuestions.reduce((sum, q) => sum + (q.degree || 0), 0);
    },
    filteredQuestions() {
      const q = (this.searchQuery || '').toString().trim().toLowerCase();
      if (!q) return this.questions;
      return this.questions.filter(item => {
        const title = (item.title || '').toString().toLowerCase();
        const type = (item.type || '').toString().toLowerCase();
        const degree = (item.degree || '').toString().toLowerCase();
        const difficulty = (this.difficultyLabel(item.difficulty_level) || '').toString().toLowerCase();
        return title.includes(q) || type.includes(q) || degree.includes(q) || difficulty.includes(q);
      });
    }
  },
  methods: {
    fetchBranch() {
      const params = this.isCourseScope
          ? { type: 1, course_id: this.courseId }
          : { type: 1, academic_year_id: this.gradeId };
      return axios.get('/lessons', { params })
          .then(res => this.branches = res.data.data)
          .catch(() => this.branches = []);
    },
    fetchUnits() {
      if (!this.branch?.id) return Promise.resolve();
      const params = this.isCourseScope
          ? { type: 2, course_id: this.courseId, parent_id: this.branch.id }
          : { type: 2, academic_year_id: this.gradeId, parent_id: this.branch.id };
      return axios.get('/lessons', { params })
        .then(res => { this.units = res.data.data; })
        .catch(() => this.units = []);
    },
    fetchLessons() {
      if (!this.unit?.id) return Promise.resolve();
      const params = this.isCourseScope
          ? { type: 3, course_id: this.courseId, parent_id: this.unit.id }
          : { type: 3, academic_year_id: this.gradeId, parent_id: this.unit.id };
      return axios.get('/lessons', { params })
          .then(res => { this.lessons = res.data.data; })
          .catch(() => this.lessons = []);
    },
    fetchQuestions() {
      const params = this.isCourseScope ? { course_id: this.courseId } : { academic_year_id: this.gradeId };
      if (this.lesson?.id) {
        params.lesson_id = this.lesson.id;
      }

      return axios.get('/questions', { params })
          .then(res => this.questions = res.data.data)
          .catch(() => this.questions = []);
    },
    parseTimeString(str) {
      if (!str) return null;
      const [h, m] = str.split(':').map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      const d = new Date();
      d.setHours(h, m, 0, 0);
      return d;
    },
    formatDate(date) {
      if (!date) return "";
      let d = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(d)) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatTimeForBackend(date, use24Hour = false) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d)) return '';
      const hours24 = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, '0');
      if (use24Hour) {
        return `${hours24.toString().padStart(2, '0')}:${minutes}`;
      }
      let hours = hours24 % 12;
      hours = hours ? hours : 12;
      const ampm = hours24 >= 12 ? 'pm' : 'am';
      return `${hours}:${minutes} ${ampm}`;
    },
    difficultyLabel(value) {
      const map = { easy: 'سهل', medium: 'متوسط', hard: 'صعب' };
      return map[value] || value || '';
    },
    async fetchExam() {
      try {
        const { data } = await axios.get(`/admin/exams/${this.examId}`);
        const exam = data.data;
        this.title = exam.title ?? '';
        this.date = exam.date ? new Date(exam.date) : null;
        this.duration_required = !!exam.duration_required;
        this.from_time = this.parseTimeString(exam.from_time);
        this.to_time = this.parseTimeString(exam.to_time);
        this.selectedQuestions = exam.questions ?? [];

        // Resolve the branch/unit/lesson chain the exam's lesson belongs to (if any).
        const examLesson = exam.lesson;
        await this.fetchBranch();
        if (examLesson?.parent?.parent) {
          this.branch = this.branches.find(b => b.id === examLesson.parent.parent.id) || null;
        } else if (examLesson?.parent) {
          this.branch = this.branches.find(b => b.id === examLesson.parent.id) || null;
        }
        if (this.branch) {
          await this.fetchUnits();
          if (examLesson?.parent) {
            this.unit = this.units.find(u => u.id === examLesson.parent.id) || null;
          }
          if (this.unit) {
            await this.fetchLessons();
            this.lesson = this.lessons.find(l => l.id === examLesson.id) || null;
          }
        }
        await this.fetchQuestions();
        // Keep already-selected questions even if they fall outside the current
        // branch/unit/lesson filter (e.g. a mixed-lesson exam).
        const known = new Map(this.questions.map(q => [q.id, q]));
        this.selectedQuestions.forEach(q => { if (!known.has(q.id)) known.set(q.id, q); });
        this.selectedQuestions = this.selectedQuestions.map(q => known.get(q.id) || q);
      } catch (error) {
        Swal.fire({ icon: "error", title: error.response?.data?.message || "تعذر تحميل بيانات الامتحان" });
      } finally {
        this.loading = false;
      }
    },
    sendForm() {
      this.v$.$validate();
      if (this.v$.$error) return;
      if (!this.date || this.question_ids.length === 0) {
        Swal.fire({ icon: "warning", title: "يرجى ملء الحقول المطلوبة" });
        return;
      }
      this.formSending = true;

      const payload = {
        title: this.title,
        date: this.formatDate(this.date),
        total_degree: this.totalDegree,
        duration_required: this.duration_required ? 1 : 0,
        question_ids: this.question_ids,
      };
      if (this.isCourseScope) {
        payload.course_id = this.courseId;
      } else {
        payload.academic_year_id = this.gradeId;
      }
      if (this.duration_required) {
        payload.from_time = this.formatTimeForBackend(this.from_time, true);
        payload.to_time = this.formatTimeForBackend(this.to_time, true);
        payload.duration = this.duration;
      }
      if (this.lesson) {
        payload.lesson_id = this.lesson.id;
      }

      axios.put(`/exams/${this.examId}`, payload)
          .then(() => {
            Swal.fire({ icon: "success", title: "تم تعديل الامتحان بنجاح" });
            this.$router.push(this.basePath);
          })
          .catch(error => {
            Swal.fire({ icon: "error", title: error.response?.data?.message || "حدث خطأ" });
          })
          .finally(() => {
            this.formSending = false;
          });
    }
  },
  async mounted() {
    if (!this.isCourseScope) {
      if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
      if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    }
    await this.fetchExam();
  }
};
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon">
        <i class="pi pi-file-export"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">تعديل امتحان</h1>
        <p class="form-page-header__subtitle">تعديل بيانات الامتحان وأسئلته</p>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="sendForm" v-if="!loading">
        <!-- Location hierarchy -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-sitemap"></i></div>
            <span class="form-card__title">الموقع في المنهج</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الفرع</label>
                  <Dropdown v-model="branch" :options="branches" showClear :optionLabel="customLabelBranch" placeholder="اختر الفرع" class="w-100" @update:model-value="fetchUnits" />
                </div>
              </div>
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الوحدة</label>
                  <Dropdown v-model="unit" :options="units" showClear :optionLabel="customLabelUnit" placeholder="اختر الوحدة" class="w-100" @update:model-value="fetchLessons" />
                </div>
              </div>
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الدرس</label>
                  <Dropdown v-model="lesson" :options="lessons" showClear :optionLabel="customLabelLesson" placeholder="اختر الدرس" class="w-100" @update:model-value="fetchQuestions" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Exam settings -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-cog"></i></div>
            <span class="form-card__title">إعدادات الامتحان</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">اسم الامتحان <span class="required">*</span></label>
                  <InputText id="title" v-model="title" placeholder="أدخل اسم الامتحان" class="w-100" />
                  <span class="field-error" v-if="v$.title.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال اسم الامتحان
                  </span>
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">تاريخ الامتحان <span class="required">*</span></label>
                  <Calendar v-model="date" dateFormat="yy-mm-dd" showIcon class="w-100" />
                  <span class="field-error" v-if="v$.date.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال التاريخ
                  </span>
                </div>
              </div>
            </div>

            <div class="source-toggle mt-4">
              <i class="pi pi-clock" style="color: var(--text-secondary);"></i>
              <span class="source-toggle__label">تحديد وقت الامتحان</span>
              <InputSwitch v-model="duration_required" />
              <span class="source-toggle__hint">{{ duration_required ? 'سيتم تحديد وقت بداية ونهاية' : 'بدون توقيت محدد' }}</span>
            </div>

            <div class="row g-4 mt-2" v-if="duration_required">
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">من الساعة</label>
                  <DatePicker v-model="from_time" hourFormat="12" timeOnly fluid class="w-100" />
                </div>
              </div>
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">إلى الساعة</label>
                  <DatePicker v-model="to_time" hourFormat="12" timeOnly fluid class="w-100" />
                </div>
              </div>
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">المدة المحسوبة</label>
                  <div class="exam-duration-display">
                    <i class="pi pi-clock"></i>
                    <span>{{ duration ? `${duration} دقيقة` : '—' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Question selection -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-list-check"></i></div>
            <span class="form-card__title">اختيار الأسئلة <span style="color:var(--color-danger);font-size:0.75rem;">*</span></span>
          </div>
          <div class="form-card__body">
            <div class="exam-search-bar mb-3">
              <i class="pi pi-search exam-search-bar__icon"></i>
              <InputText v-model="searchQuery" placeholder="بحث في الأسئلة بالعنوان أو النوع أو الدرجة..." class="w-100" />
            </div>
            <DataTable
              :value="filteredQuestions"
              selectionMode="multiple"
              :selection="selectedQuestions"
              @update:selection="val => selectedQuestions = val"
              dataKey="id"
              class="w-100"
            >
              <Column selectionMode="multiple" headerStyle="width: 3em"></Column>
              <Column field="title" header="عنوان السؤال"></Column>
              <Column field="type" header="النوع"></Column>
              <Column field="degree" header="الدرجة"></Column>
              <Column header="الصعوبة">
                <template #body="{ data }">{{ difficultyLabel(data.difficulty_level) }}</template>
              </Column>
            </DataTable>
            <span class="field-error mt-2" v-if="v$.question_ids.$error">
              <i class="pi pi-exclamation-circle"></i> برجاء اختيار الأسئلة
            </span>

            <div class="exam-summary-card mt-4" v-if="selectedQuestions.length > 0">
              <div class="exam-summary-card__item">
                <span class="exam-summary-card__label">عدد الأسئلة</span>
                <span class="exam-summary-card__value">{{ selectedQuestions.length }}</span>
              </div>
              <div class="exam-summary-card__divider"></div>
              <div class="exam-summary-card__item">
                <span class="exam-summary-card__label">مجموع الدرجات</span>
                <span class="exam-summary-card__value exam-summary-card__value--primary">{{ totalDegree }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الحفظ...' : 'حفظ التعديلات' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
